import { defaultThemeV2 } from "../../../../theme/defaultThemeV2";
import type { SurveyCopy } from "../../../../theme/copy";
import { FC, useEffect, useRef, useState } from "react";
import Hls from "hls.js";
import { playPauseButtonStyle } from "./style";

interface HlsProps {
  src: string;
  copy?: SurveyCopy;
  style: React.CSSProperties;
}

const HlsVideoPlayer: FC<HlsProps> = ({
  src,
  style,
  copy = defaultThemeV2.copy,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showButton, setShowButton] = useState(true);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /* c8 ignore start */
  const handlePlay = () => {
    if (videoRef.current) {
      videoRef.current.play();
    }
  };

  const handlePause = () => {
    if (videoRef.current) {
      videoRef.current.pause();
    }
  };

  useEffect(() => {
    const onPlay = () => {
      setIsPlaying(true);
      setShowButton(false);
    };
    const onPause = () => {
      setIsPlaying(false);
      setShowButton(true);
    };

    if (videoRef.current && Hls.isSupported()) {
      const hls = new Hls();
      const videoElement = videoRef.current;
      hls.loadSource(src);
      hls.attachMedia(videoElement);

      videoElement.addEventListener("play", onPlay);
      videoElement.addEventListener("pause", onPause);

      return () => {
        hls.destroy();
        videoElement.removeEventListener("play", onPlay);
        videoElement.removeEventListener("pause", onPause);
      };
    }
  }, [src]);

  const handleMouseEnter = () => {
    if (isPlaying) {
      if (timerRef.current) clearTimeout(timerRef.current);
      setShowButton(true);
    }
  };
  const handleMouseLeave = () => {
    if (isPlaying)
      timerRef.current = setTimeout(() => setShowButton(false), 3000);
  };
  /* c8 ignore end */
  return (
    <section
      aria-label={copy.labels.hlsVideo}
      style={{ position: "relative" }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <video
        ref={videoRef}
        controls={false}
        controlsList="nofullscreen"
        playsInline
        autoFocus
        style={style}
      ></video>
      {
        /* c8 ignore start */ showButton && (
          <>
            {isPlaying ? (
              <button
                onClick={handlePause}
                aria-label={copy.actions.pauseVideo}
                data-testid={"hls-pause-button"}
                style={playPauseButtonStyle}
              >
                ▌▌
              </button>
            ) : (
              <button
                onClick={handlePlay}
                aria-label={copy.actions.playVideo}
                data-testid={"hls-play-button"}
                style={playPauseButtonStyle}
              >
                ▶
              </button>
              /* c8 ignore end */
            )}
          </>
        )
      }
    </section>
  );
};

export default HlsVideoPlayer;
